import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import NotificationBell from './agent/NotificationBell';
import ExternalAppsNav from './ExternalAppsNav';
import { searchUnified, fetchMe, type UnifiedSearchResult } from '../lib/api';
import {
  Activity,
  ArrowLeftRight,
  Banknote,
  Bitcoin,
  BookOpen,
  Briefcase,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  FolderOpen,
  Gavel,
  History,
  LayoutDashboard,
  Lock,
  Menu,
  Monitor,
  Receipt,
  Scale,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  User,
  Webhook,
  X,
} from 'lucide-react';

interface Module {
  to: string;
  icon: React.ElementType;
  label: string;
  shortLabel?: string;
}

interface NavGroup {
  id: string;
  label: string;
  items: Module[];
}

const aiModule: Module = {
  to: '/ai',
  icon: Sparkles,
  label: 'AI Console',
  shortLabel: 'AI',
};

const pinModules: Module[] = [
  { to: '/overview', icon: LayoutDashboard, label: 'Overview' },
  { to: '/approvals', icon: ShieldCheck, label: 'Approvals' },
];

const navGroups: NavGroup[] = [
  {
    id: 'work',
    label: 'Work',
    items: [
      { to: '/transactions', icon: ArrowLeftRight, label: 'Transactions' },
      { to: '/invoices', icon: FileText, label: 'Invoices' },
      { to: '/contracts', icon: Briefcase, label: 'Contracts' },
      { to: '/receipts', icon: Receipt, label: 'Receipts' },
      { to: '/crm', icon: Building2, label: 'CRM / Clients', shortLabel: 'CRM' },
      { to: '/files', icon: FolderOpen, label: 'Files' },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      { to: '/mercury', icon: Banknote, label: 'Mercury' },
      { to: '/paypal', icon: Scale, label: 'PayPal' },
      { to: '/stripe', icon: CreditCard, label: 'Stripe' },
      { to: '/chain2pay', icon: Activity, label: 'Chain2Pay' },
      { to: '/crypto', icon: Bitcoin, label: 'Crypto' },
      { to: '/assets', icon: Gavel, label: 'Assets' },
      { to: '/tax', icon: Lock, label: 'Tax' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { to: '/mcp', icon: Monitor, label: 'MCP Console', shortLabel: 'MCP' },
      { to: '/webhooks', icon: Webhook, label: 'Webhooks' },
      { to: '/backups', icon: History, label: 'Backups' },
      { to: '/admin', icon: Settings, label: 'Admin' },
      { to: '/api-docs', icon: BookOpen, label: 'API Docs' },
    ],
  },
];

const allModules: Module[] = [aiModule, ...pinModules, ...navGroups.flatMap((g) => g.items)];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

function navLinkClass(isActive: boolean, sidebarOpen: boolean, prominent = false) {
  return [
    'flex min-h-9 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium leading-snug transition-colors',
    prominent && 'min-h-10 py-2',
    isActive
      ? prominent
        ? 'bg-zt-primary/15 text-zt-accent ring-1 ring-zt-primary/35'
        : 'bg-zt-elevated text-zt-accent'
      : prominent
        ? 'text-slate-200 hover:bg-zt-elevated hover:text-white'
        : 'text-zt-muted hover:bg-zt-elevated hover:text-slate-200',
    !sidebarOpen && 'justify-center px-2',
  ].join(' ');
}

function Layout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isAiRoute = pathname === '/ai' || pathname.startsWith('/ai/');

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile && !isAiRoute);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [entityResults, setEntityResults] = useState<UnifiedSearchResult[]>([]);
  const [authLabel, setAuthLabel] = useState('checking…');
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const g of navGroups) {
      initial[g.id] = g.items.some(
        (m) => pathname === m.to || (m.to !== '/' && pathname.startsWith(m.to))
      );
    }
    return initial;
  });

  useEffect(() => {
    let cancelled = false;
    fetchMe().then((result) => {
      if (cancelled) return;
      if (result.ok && result.data) {
        const name =
          result.data.preferred_username ||
          result.data.name ||
          result.data.email ||
          'signed in';
        setAuthLabel(name === 'Service API Key' ? 'API session' : name);
      } else if (result.status === 401) {
        setAuthLabel('sign in required');
      } else {
        setAuthLabel('session unknown');
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // On AI: collapse shell chrome for chat viewport. Elsewhere: restore desktop sidebar.
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setMobileMenuOpen(false);
      return;
    }
    setSidebarOpen(!isAiRoute);
  }, [isMobile, isAiRoute]);

  // Auto-expand the group that contains the active route.
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const g of navGroups) {
        if (g.items.some((m) => pathname === m.to || pathname.startsWith(`${m.to}/`))) {
          next[g.id] = true;
        }
      }
      return next;
    });
  }, [pathname]);

  const title = useMemo(() => {
    if (isAiRoute) return 'AI Console';
    return allModules.find((m) => m.to === pathname)?.label ?? 'Dashboard';
  }, [pathname, isAiRoute]);

  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return allModules.filter(
      (m) =>
        m.label.toLowerCase().includes(q) ||
        m.to.replace('/', '').toLowerCase().includes(q)
    );
  }, [search]);

  const loadEntitySearch = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setEntityResults([]);
      return;
    }
    const response = await searchUnified({ q: trimmed, limit: 8 });
    setEntityResults(response.data?.data?.results ?? []);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadEntitySearch(search);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search, loadEntitySearch]);

  const hasDropdownResults = searchFocused && (entityResults.length > 0 || searchResults.length > 0);

  const handleSearchSelect = (to: string) => {
    setSearch('');
    setEntityResults([]);
    setSearchFocused(false);
    navigate(to);
    if (isMobile) setMobileMenuOpen(false);
  };

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const sidebarWidth = sidebarOpen ? 'w-64' : 'w-16';
  const desktopSidebar = !isMobile;

  const renderNavItem = (m: Module, opts?: { prominent?: boolean; end?: boolean }) => (
    <NavLink
      key={m.to}
      to={m.to}
      end={opts?.end ?? (m.to === '/ai' || m.to === '/overview')}
      onClick={() => isMobile && setMobileMenuOpen(false)}
      className={({ isActive }) => navLinkClass(isActive, sidebarOpen, opts?.prominent)}
      title={
        !sidebarOpen || (m.shortLabel && m.shortLabel !== m.label) ? m.label : undefined
      }
    >
      <m.icon size={opts?.prominent ? 18 : 17} className="shrink-0" aria-hidden="true" />
      {sidebarOpen && <span className="truncate">{m.shortLabel ?? m.label}</span>}
    </NavLink>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-zt-bg">
      {isMobile && mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          'flex flex-col border-r border-zt-border bg-zt-surface transition-all duration-200',
          'fixed z-50 h-full lg:static',
          sidebarWidth,
          isMobile && !mobileMenuOpen ? '-translate-x-full' : 'translate-x-0',
        ].join(' ')}
        aria-label="Main navigation"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-zt-border px-3 py-2.5">
          <Link
            to="/ai"
            className={[
              'flex min-w-0 items-center gap-2.5',
              sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
            ].join(' ')}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-zt-primary to-zt-accent text-zt-bg">
              <ShieldCheck size={18} />
            </div>
            <span className="truncate text-base font-semibold tracking-tight text-white">
              ZeroTail
            </span>
            <span className="shrink-0 rounded border border-zt-border bg-zt-bg px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zt-muted">
              Internal
            </span>
          </Link>
          {desktopSidebar && (
            <button
              type="button"
              onClick={() => setSidebarOpen((s) => !s)}
              className="rounded p-1.5 text-zt-muted hover:bg-zt-elevated hover:text-slate-200"
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="rounded p-1.5 text-zt-muted hover:bg-zt-elevated hover:text-slate-200"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-2.5">
          <nav className="flex flex-col gap-1" aria-label="Primary">
            {renderNavItem(aiModule, { prominent: true, end: false })}

            {sidebarOpen && (
              <p className="mb-0.5 mt-3 px-2.5 text-[10px] font-semibold uppercase tracking-wide text-zt-muted">
                Quick
              </p>
            )}
            {!sidebarOpen && <div className="my-1 border-t border-zt-border" />}
            {pinModules.map((m) => renderNavItem(m))}
          </nav>

          <nav className="mt-3 flex flex-col gap-1" aria-label="Modules">
            {navGroups.map((group) => {
              const expanded = openGroups[group.id] ?? false;
              const groupActive = group.items.some(
                (m) => pathname === m.to || pathname.startsWith(`${m.to}/`)
              );

              if (!sidebarOpen) {
                return (
                  <div key={group.id} className="flex flex-col gap-1">
                    <div className="my-1 border-t border-zt-border" />
                    {group.items.map((m) => renderNavItem(m))}
                  </div>
                );
              }

              return (
                <div key={group.id} className="mt-1">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className={[
                      'mb-0.5 flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide transition-colors',
                      groupActive
                        ? 'text-slate-300 hover:bg-zt-elevated'
                        : 'text-zt-muted hover:bg-zt-elevated hover:text-slate-200',
                    ].join(' ')}
                    aria-expanded={expanded}
                  >
                    <span>{group.label}</span>
                    <ChevronDown
                      size={12}
                      className={`transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`}
                    />
                  </button>
                  {expanded && (
                    <div className="flex flex-col gap-0.5">{group.items.map((m) => renderNavItem(m))}</div>
                  )}
                </div>
              );
            })}
          </nav>

          <ExternalAppsNav expanded={sidebarOpen} />
        </div>

        <div className="shrink-0 border-t border-zt-border px-3 py-2.5 text-xs text-zt-muted">
          <div className="flex items-center gap-2">
            <div
              className={[
                'h-2 w-2 shrink-0 rounded-full',
                authLabel === 'sign in required' || authLabel === 'session unknown'
                  ? 'bg-zt-warning'
                  : 'bg-zt-primary',
              ].join(' ')}
            />
            {sidebarOpen && <span className="truncate">Operator · {authLabel}</span>}
          </div>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <header
          className={[
            'flex items-center justify-between gap-4 border-b border-zt-border bg-zt-surface',
            isAiRoute ? 'px-3 py-2 lg:px-4' : 'px-4 py-3 lg:px-6',
          ].join(' ')}
        >
          <div className="flex min-w-0 items-center gap-3">
            {isMobile && (
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="rounded p-1 text-zt-muted hover:bg-zt-elevated hover:text-slate-200"
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>
            )}
            {isAiRoute ? (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">AI Console</p>
                <p className="hidden truncate text-[11px] text-zt-muted sm:block">
                  Ask, attach, approve — work starts here
                </p>
              </div>
            ) : (
              <>
                <h1 className="text-lg font-semibold text-white lg:text-xl">{title}</h1>
                <span className="hidden rounded-md border border-zt-primary/30 bg-zt-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zt-primary sm:inline">
                  Internal
                </span>
              </>
            )}
          </div>

          <div className="flex flex-1 items-center justify-end gap-3 lg:gap-4">
            <div className={`relative hidden w-full sm:block ${isAiRoute ? 'max-w-xs' : 'max-w-md'}`}>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search size={16} className="text-zt-muted" />
              </div>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                placeholder={isAiRoute ? 'Jump to a module…' : 'Search invoices, clients, docs...'}
                className={[
                  'w-full rounded-lg border border-zt-border bg-zt-bg py-2 pl-10 pr-4 text-sm text-slate-200 placeholder-zt-muted focus:border-zt-primary focus:outline-none focus:ring-1 focus:ring-zt-primary',
                  isAiRoute && 'py-1.5 text-xs',
                ].join(' ')}
              />
              {hasDropdownResults && (
                <div className="absolute top-full z-30 mt-2 w-full rounded-lg border border-zt-border bg-zt-elevated shadow-lg">
                  {entityResults.length > 0 && (
                    <div className="border-b border-zt-border px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-zt-muted">
                      Records
                    </div>
                  )}
                  {entityResults.map((item) => (
                    <button
                      key={`${item.type}-${item.id}`}
                      type="button"
                      onMouseDown={() => handleSearchSelect(item.url || '/ai')}
                      className="flex w-full flex-col gap-0.5 px-4 py-2 text-left text-sm text-slate-200 hover:bg-zt-bg"
                    >
                      <span className="font-medium">{item.title}</span>
                      <span className="text-xs text-zt-muted">
                        {item.type}
                        {item.subtitle ? ` · ${item.subtitle}` : ''}
                      </span>
                    </button>
                  ))}
                  {searchResults.length > 0 && (
                    <div className="border-b border-zt-border px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-zt-muted">
                      Modules
                    </div>
                  )}
                  {searchResults.map((m) => (
                    <button
                      key={m.to}
                      type="button"
                      onMouseDown={() => handleSearchSelect(m.to)}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-slate-200 hover:bg-zt-bg"
                    >
                      <m.icon size={16} className="text-zt-primary" />
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <NotificationBell />

            <div className="group relative">
              <button
                type="button"
                className={[
                  'flex items-center gap-2 rounded-lg border border-zt-border bg-zt-bg text-sm font-medium text-slate-200 hover:border-zt-primary hover:text-zt-primary',
                  isAiRoute ? 'px-2.5 py-1.5' : 'px-3 py-2',
                ].join(' ')}
                aria-label="User menu"
              >
                <User size={18} />
                <span className="hidden sm:inline">Operator</span>
              </button>
              <div className="absolute right-0 top-full z-30 mt-2 hidden w-48 rounded-lg border border-zt-border bg-zt-elevated py-1 shadow-lg group-hover:block">
                <button
                  type="button"
                  className="block w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-zt-bg"
                >
                  Profile
                </button>
                <button
                  type="button"
                  className="block w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-zt-bg"
                >
                  Settings
                </button>
                <div className="my-1 border-t border-zt-border" />
                <button
                  type="button"
                  className="block w-full px-4 py-2 text-left text-sm text-zt-danger hover:bg-zt-bg"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </header>

        <div
          className={
            isAiRoute
              ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
              : 'min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6'
          }
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default Layout;
